import * as React from "react";
import { Modal, Skeleton, Table, Input, Space, message } from "antd";
import { DatasetApiCommunicator } from "api/communicators/DatasetApiCommunicator";
import { formatFullDateFromTimeStamp } from "api/utils";
import { useSearch } from "hooks";

/**
 * AddDatasetModal Component
 * 
 * Modal component for selecting and adding datasets to other entities.
 * Provides search functionality and multi-select capabilities for dataset management.
 * 
 * Features:
 * - Dataset search functionality
 * - Multi-select dataset selection
 * - Real-time search with debouncing
 * - Loading state management
 * - Error handling
 * - Pre-selected dataset support
 * 
 * @param {Object} props - Component props
 * @param {boolean} [props.open=false] - Whether the modal is open
 * @param {Array} [props.startingDatasetIdSelection=[]] - Pre-selected dataset IDs
 * @param {Function} props.onClose - Callback for modal close
 * @param {Function} props.onConfirm - Callback for confirmation with selected dataset IDs
 * @returns {JSX.Element} The add dataset modal component
 */
export function AddDatasetModal({
  open = false,
  startingDatasetIdSelection = [],
  onClose,
  onConfirm,
}) {
  const [datasetApiCommunicator] = React.useState(new DatasetApiCommunicator());
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = React.useState(
    startingDatasetIdSelection
  );

  const [messageApi, contextHolder] = message.useMessage();

  const errorToast = React.useCallback(
    (text) => messageApi.error(text),
    [messageApi]
  );

  const initializeModalState = React.useCallback((open) => {
    if (open) {
      setSelectedRowKeys(startingDatasetIdSelection)
    }
  }, [startingDatasetIdSelection])

  const {
    query: searchTerm,
    setQuery: setSearchTerm,
    results: searchResults,
    loading: isSearching,
    search,
  } = useSearch(true, 500, "dataset", errorToast);

  const doFetch = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await datasetApiCommunicator.list();
      const descSorted = res.data.sort((a, b) => b.dateAdded - a.dateAdded);
      setResults(descSorted);
    } catch (err) {}
    setLoading(false);
  }, [datasetApiCommunicator, setLoading, setResults]);

  React.useEffect(() => {
    doFetch();
  }, [datasetApiCommunicator, doFetch]);

  const onSelectChange = React.useCallback(
    async (newSelectedRowKeys) => {
      console.log("Selected Row Keys changed: ", newSelectedRowKeys);
      setSelectedRowKeys(newSelectedRowKeys);
      const selectedRecords = results.filter((record) =>
        newSelectedRowKeys.includes(record.datasetId)
      );
      console.log("Selected Records: ", selectedRecords);
    },
    [setSelectedRowKeys, results]
  );

  const columns = [
    {
      title: "Name",
      key: "datasetName",
      render: (_, r) => <div>{r.datasetName}</div>,
    },
    {
      title: "Date",
      key: "dateAdded",
      render: (_, r) => <div>{formatFullDateFromTimeStamp(r.dateAdded)}</div>,
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const mapSelectionAndConfirmUpdate = React.useCallback(() => {
    onConfirm(selectedRowKeys); // modelIds
  }, [selectedRowKeys, onConfirm]);

  const dataSource = searchTerm && searchResults ? searchResults : results;

  return (
    <Modal
      width={1000}
      onCancel={onClose}
      open={open}
      onOk={mapSelectionAndConfirmUpdate}
      afterOpenChange={initializeModalState}
    >
      {contextHolder}
      <Space>
        <h3>Datasets</h3>
        <Input.Search
          placeholder="search datasets"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
          }}
          onSearch={search}
          loading={isSearching}
        />
      </Space>
      <Skeleton loading={loading}>
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={dataSource}
          rowKey={(record) => record.datasetId}
          locale={{
            emptyText: "No datasets found",
          }}
        />
      </Skeleton>
    </Modal>
  );
}
